import { useMemo } from "react";
import { useEgdHubView } from "src/hooks/useEgdHubView";
import { useEgdHubData } from "src/hooks/useEgdHubData";
import { isPayloadEgdSource } from "src/utils/egdSource";
import {
  getHighestJackpotForGame,
  getJackpotGames,
} from "src/utils/gameListUtils";

/**
 * @typedef {import('src/api/payload/egd').EgdHubViewSlug} EgdHubViewSlug
 * @typedef {import('src/api/payload/egd').EgdHubCard} EgdHubCard
 */

const ersCardsForView = (view, gameList, jackpotList) => {
  if (!Array.isArray(gameList) || !Array.isArray(jackpotList)) return [];

  if (view === "progressive-jackpots") {
    return (getJackpotGames(jackpotList, gameList) || []).map((entry) => ({
      game: entry.game,
      jackpot: entry,
    }));
  }

  const statusFilter = view === "cny" ? "CNY" : "new";

  return gameList
    .filter((game) => game.status === statusFilter)
    .map((game) => ({
      game,
      jackpot: getHighestJackpotForGame(game, jackpotList),
    }));
};

/**
 * Unified EGD hub cards — Payload hub API or legacy ERS bulk fetch + client filter.
 *
 * @param {EgdHubViewSlug} view
 * @param {'en' | 'fr'} locale
 * @returns {{
 *   cards: EgdHubCard[],
 *   progressiveJackpotThreshold: number | null,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useEgdHubCards(view, locale) {
  const usePayload = isPayloadEgdSource();

  const payloadHub = useEgdHubView(view, locale, { enabled: usePayload });
  const ersData = useEgdHubData(locale, { enabled: !usePayload });

  const ersCards = useMemo(
    () => ersCardsForView(view, ersData.gameList, ersData.jackpotList),
    [view, ersData.gameList, ersData.jackpotList]
  );

  if (usePayload) {
    return {
      cards: payloadHub.cards,
      progressiveJackpotThreshold: payloadHub.progressiveJackpotThreshold,
      loading: payloadHub.loading,
      error: payloadHub.error,
    };
  }

  return {
    cards: ersCards,
    progressiveJackpotThreshold: null,
    loading: ersData.loading,
    error: ersData.error,
  };
}
